import React, { useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_TEXT_TYPES = [0, 5]; // text + announcement
const CATEGORY_TYPES = [4];

function channelLabel(ch, kind) {
  if (!ch) return "";
  if (kind === "category" || ch.type === 4) return ch.name;
  if (kind === "voice" || ch.type === 2) return `🔊 ${ch.name}`;
  return `# ${ch.name}`;
}

function matchesQuery(ch, query, kind) {
  if (!query) return true;
  const q = query.toLowerCase();
  const label = channelLabel(ch, kind).toLowerCase();
  return label.includes(q) || String(ch.id).includes(q);
}

/**
 * Searchable channel/category picker used across settings.
 * - single: dropdown that looks like settings-input, type to filter
 * - multiple: same dropdown + selected chips
 */
export default function ChannelPicker({
  channels = [],
  value,
  onChange,
  multiple = false,
  kind = "text", // "text" | "category" | "voice" | "all"
  types,
  placeholder,
  emptyLabel = "None",
  allowEmpty = true,
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const options = useMemo(() => {
    const typeFilter =
      types ||
      (kind === "category"
        ? CATEGORY_TYPES
        : kind === "voice"
          ? [2]
          : kind === "all"
            ? null
            : DEFAULT_TEXT_TYPES);
    const list = Array.isArray(channels) ? channels : [];
    const filtered = typeFilter
      ? list.filter((ch) => typeFilter.includes(ch.type))
      : list;
    return filtered.filter((ch) => matchesQuery(ch, query, kind));
  }, [channels, types, query, kind]);

  const selectedIds = multiple
    ? Array.isArray(value)
      ? value.map(String)
      : []
    : value
      ? [String(value)]
      : [];

  const selectedChannel = !multiple
    ? channels.find((ch) => String(ch.id) === String(value || ""))
    : null;

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (!rootRef.current?.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const displayPlaceholder =
    placeholder ||
    (kind === "category"
      ? "Select a category..."
      : kind === "voice"
        ? "Select a voice channel..."
        : "Select a channel...");

  const showTriggerText = multiple
    ? selectedIds.length
      ? `${selectedIds.length} selected`
      : displayPlaceholder
    : selectedChannel
      ? channelLabel(selectedChannel, kind)
      : displayPlaceholder;

  const notifyDirty = () => {
    rootRef.current?.dispatchEvent(
      new CustomEvent("settings-dirty", { bubbles: true }),
    );
  };

  const pick = (id) => {
    if (multiple) {
      const next = selectedIds.includes(String(id))
        ? selectedIds.filter((x) => x !== String(id))
        : [...selectedIds, String(id)];
      onChange(next);
      setQuery("");
      inputRef.current?.focus();
      notifyDirty();
      return;
    }
    onChange(id);
    setOpen(false);
    setQuery("");
    notifyDirty();
  };

  const clear = (e) => {
    e.stopPropagation();
    if (multiple) onChange([]);
    else onChange("");
    setQuery("");
    notifyDirty();
  };

  const removeChip = (id) => {
    if (!multiple) return;
    onChange(selectedIds.filter((x) => x !== String(id)));
    notifyDirty();
  };

  return (
    <div
      className={`channel-picker ${disabled ? "is-disabled" : ""} ${className}`}
      ref={rootRef}
    >
      <button
        type="button"
        className={`channel-picker-trigger settings-input ${open ? "is-open" : ""}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
      >
        <span
          className={`channel-picker-value ${
            !selectedChannel && !selectedIds.length ? "is-placeholder" : ""
          }`}
        >
          {showTriggerText}
        </span>
        <span className="channel-picker-actions">
          {allowEmpty &&
            (multiple ? selectedIds.length > 0 : Boolean(value)) && (
              <span
                className="channel-picker-clear"
                role="button"
                tabIndex={-1}
                onClick={clear}
                aria-label="Clear selection"
              >
                ×
              </span>
            )}
          <span className="channel-picker-caret" aria-hidden />
        </span>
      </button>

      {open && (
        <div className="channel-picker-menu" role="listbox">
          <input
            ref={inputRef}
            type="text"
            className="channel-picker-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={
              kind === "category"
                ? "Type to filter categories…"
                : kind === "voice"
                  ? "Type to filter voice channels…"
                  : "Type to filter channels…"
            }
            onClick={(e) => e.stopPropagation()}
          />
          <div className="channel-picker-options">
            {allowEmpty && !multiple && (
              <button
                type="button"
                className={`channel-picker-option ${!value ? "is-selected" : ""}`}
                onClick={() => pick("")}
              >
                {emptyLabel}
              </button>
            )}
            {options.length === 0 && (
              <div className="channel-picker-empty">No matches</div>
            )}
            {options.map((ch) => {
              const selected = selectedIds.includes(String(ch.id));
              return (
                <button
                  type="button"
                  key={ch.id}
                  className={`channel-picker-option ${selected ? "is-selected" : ""}`}
                  onClick={() => pick(ch.id)}
                >
                  {multiple && (
                    <span className="channel-picker-check" aria-hidden>
                      {selected ? "✓" : ""}
                    </span>
                  )}
                  <span>{channelLabel(ch, kind)}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {multiple && selectedIds.length > 0 && (
        <div className="channel-picker-chips">
          {selectedIds.map((id) => {
            const ch = channels.find((c) => String(c.id) === String(id));
            return (
              <span key={id} className="channel-picker-chip">
                {ch ? channelLabel(ch, kind) : id}
                <button
                  type="button"
                  className="channel-picker-chip-remove"
                  onClick={() => removeChip(id)}
                  aria-label={`Remove ${ch?.name || id}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}
